import { useState, useEffect, useCallback } from "react";
import { Trophy, Flame } from "lucide-react";
import { api } from "@/lib/api";

/**
 * 🏅 AchievementsCard — Trader DNA (discipline vs risk persona) + badge wall.
 * Scored on behavior (stake sizing, budget use, consistency) — never on profit.
 */
const AchievementsCard = ({ accountId }) => {
  const [data, setData] = useState(null);
  const [celebrate, setCelebrate] = useState(null); // freshly unlocked badge

  const load = useCallback(async () => {
    if (!accountId) return;
    try {
      const res = await api.get(`/achievements/${accountId}`);
      setData(res.data);
      // 🎉 celebrate NEW unlocks only (first visit just records, no confetti spam)
      const unlocked = (res.data?.badges || []).filter((b) => b.unlocked).map((b) => b.id);
      const key = `vts_badges_seen_${accountId}`;
      let seen = null;
      try { seen = JSON.parse(localStorage.getItem(key) || "null"); } catch { /* ignore */ }
      if (Array.isArray(seen)) {
        const fresh = (res.data?.badges || []).find((b) => b.unlocked && !seen.includes(b.id));
        if (fresh) setCelebrate(fresh);
      }
      localStorage.setItem(key, JSON.stringify(unlocked));
    } catch { /* ignore */ }
  }, [accountId]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!celebrate) return;
    const t = setTimeout(() => setCelebrate(null), 8000);
    return () => clearTimeout(t);
  }, [celebrate]);

  if (!accountId) {
    return (
      <div data-testid="achievements-card" className="rounded-lg border border-vts-line bg-vts-panel p-4">
        <p className="text-xs font-mono text-vts-silver/50">Connect your account to see your achievements 🏅</p>
      </div>
    );
  }
  if (!data) return null;

  const { persona, streaks, stats, badges, unlocked_count, total_count } = data;
  const score = persona?.score ?? 0;
  const barColor = score >= 70 ? "bg-emerald-400" : score >= 40 ? "bg-amber-400" : "bg-red-400";

  return (
    <div data-testid="achievements-card" className="rounded-lg border border-vts-line bg-vts-panel p-4 space-y-4">
      {/* 🎉 badge unlock celebration */}
      {celebrate && (
        <div data-testid="badge-celebration" className="fixed inset-0 z-[90] flex items-center justify-center p-4"
          style={{ background: "rgba(3,7,15,0.72)", backdropFilter: "blur(4px)" }}
          onClick={() => setCelebrate(null)}>
          <style>{`@keyframes vts-confetti-fall{0%{transform:translateY(-10vh) rotate(0)}100%{transform:translateY(110vh) rotate(720deg)}}`}</style>
          {Array.from({ length: 40 }).map((_, i) => (
            <span key={i} aria-hidden className="absolute top-0 rounded-sm"
              style={{
                left: `${(i * 61) % 100}%`, width: 8, height: 12,
                background: ["#facc15", "#22c55e", "#3b82f6", "#f472b6", "#f97316"][i % 5],
                animation: `vts-confetti-fall ${2.4 + (i % 5) * 0.5}s linear ${(i % 8) * 0.25}s infinite`,
              }} />
          ))}
          <div className="relative w-full max-w-xs rounded-2xl border border-amber-400/50 bg-gradient-to-b from-[#241c07] via-[#0b1220] to-[#05080f] p-6 text-center space-y-2 shadow-2xl animate-in zoom-in-95 duration-300"
            onClick={(e) => e.stopPropagation()}>
            <button data-testid="badge-celebration-close" onClick={() => setCelebrate(null)}
              className="absolute top-2 right-3 text-vts-silver/50 hover:text-white text-sm">✕</button>
            <p className="text-[10px] uppercase tracking-[0.25em] text-amber-300 font-bold">Badge unlocked!</p>
            <div className="text-5xl">{celebrate.icon}</div>
            <p data-testid="badge-celebration-name" className="text-lg font-bold text-white">{celebrate.name}</p>
            <p className="text-[11px] font-mono text-vts-silver/60">{celebrate.desc}</p>
          </div>
        </div>
      )}

      {/* 🧬 Trader DNA */}
      <div data-testid="trader-dna" className="space-y-2">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <span className="text-2xl">{persona?.icon}</span>
            <div>
              <p data-testid="dna-persona-label" className="text-sm font-bold text-white">{persona?.label}</p>
              <p className="text-[10px] font-mono text-vts-silver/50">Trader DNA · behavior only, never profit</p>
            </div>
          </div>
          <span data-testid="dna-score" className="px-2 py-1 rounded bg-vts-surface border border-vts-line text-[12px] font-bold font-mono text-white">
            {score}/100
          </span>
        </div>
        <div className="h-2 rounded-full bg-vts-surface border border-vts-line overflow-hidden">
          <div data-testid="dna-score-bar" className={`h-full rounded-full ${barColor} transition-all duration-700`} style={{ width: `${score}%` }} />
        </div>
        <div className="flex justify-between text-[9px] font-mono text-vts-silver/40">
          <span>🔥 Risk Taker</span><span>⚖️ Balanced</span><span>🛡️ Disciplined</span>
        </div>
        {(persona?.hints || []).map((h, i) => (
          <p key={i} data-testid={`dna-hint-${i}`} className="text-[10px] font-mono text-amber-300/80">💡 {h}</p>
        ))}
      </div>

      {/* 🔥 streak strip */}
      <div data-testid="achievements-streaks" className="grid grid-cols-4 gap-2 text-center">
        {[
          { k: "day-current", label: "Day streak", val: streaks?.day_current, icon: <Flame className="w-3 h-3 text-orange-400" /> },
          { k: "day-best", label: "Best days", val: streaks?.day_best, icon: <Flame className="w-3 h-3 text-vts-silver/40" /> },
          { k: "win-best", label: "Best wins", val: streaks?.win_best, icon: <Trophy className="w-3 h-3 text-amber-400" /> },
          { k: "bots-used", label: "Bots used", val: stats?.bots_used, icon: <span className="text-[10px]">🤖</span> },
        ].map((s) => (
          <div key={s.k} data-testid={`streak-${s.k}`} className="rounded bg-vts-surface border border-vts-line p-2">
            <div className="flex items-center justify-center gap-1">{s.icon}<span className="text-sm font-bold font-mono text-white">{s.val ?? 0}</span></div>
            <p className="text-[9px] font-mono text-vts-silver/50">{s.label}</p>
          </div>
        ))}
      </div>

      {/* 🏅 badge wall */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs font-bold text-white flex items-center gap-1"><Trophy className="w-3.5 h-3.5 text-amber-400" /> Badges</p>
          <span data-testid="badges-count" className="text-[10px] font-mono text-vts-silver/60">{unlocked_count}/{total_count} unlocked</span>
        </div>
        <div data-testid="badges-grid" className="grid grid-cols-3 sm:grid-cols-5 gap-2">
          {(badges || []).map((b) => (
            <div
              key={b.id}
              data-testid={`badge-${b.id}`}
              title={b.desc}
              className={`rounded border p-2 text-center transition-all duration-200 ${
                b.unlocked
                  ? "border-amber-400/40 bg-amber-400/10 hover:scale-105"
                  : "border-vts-line bg-vts-surface opacity-60"
              }`}
            >
              <div className={`text-xl ${b.unlocked ? "" : "grayscale"}`}>{b.icon}</div>
              <p className={`text-[9px] font-mono leading-tight mt-1 ${b.unlocked ? "text-amber-200" : "text-vts-silver/50"}`}>{b.name}</p>
              {!b.unlocked && (
                <div className="mt-1">
                  <div className="h-1 rounded-full bg-vts-line overflow-hidden">
                    <div className="h-full bg-vts-silver/40" style={{ width: `${Math.min(100, (b.progress / b.target) * 100)}%` }} />
                  </div>
                  <p className="text-[8px] font-mono text-vts-silver/40 mt-0.5">{b.progress}/{b.target}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AchievementsCard;
